// simple regex to find things that looks like urls
import {IDocumentMetadata} from "./model/i-tax-return";
import {updateDocumentCache} from "./document-cache";
import {TaxReturnService} from "../../../../swaggerServices";
import {PastedDocMetadata} from "./subcategory/subcategory";

const urlRegex = /^(file|http|https):\/\/[^\s]+/g;

export const findUrls = text => {
  const matches = [];
  let match = urlRegex.exec(text);
  while (match != null) {
    matches.push({index: match.index, url: match[0]});
    match = urlRegex.exec(text);
  }
  return matches;
};

const parseAsFilesystemUrl = urlStr => {
  try {
    const url = new URL(urlStr);
    if (url.hostname) {
      const docId = `//${url.hostname}${decodeURIComponent(url.pathname)}`;
      return docId || null;
    }
  } catch (e) {
  }
  return null;
}

const parseAsDocshareUrl = urlStr => {
  const docshareLinkPrefix = "/link/d/";
  try {
    const url = new URL(urlStr);
    const idx = url.pathname.indexOf(docshareLinkPrefix);
    if (url.hostname.endsWith(".au.kworld.kpmg.com") && idx >= 0) {
      const docId = url.pathname.slice(idx + docshareLinkPrefix.length);
      return docId || null;
    }
  } catch (e) {
  }
  return null;
};

const parseAsCndocsUrl = urlStr => {
  try {
    const url = new URL(urlStr);
    const idx = url.pathname.lastIndexOf('/');
    if (url.hostname.endsWith(".cn.kworld.kpmg.com") && idx >= 0) {
      const docId = decodeURIComponent(url.pathname);
      return docId || null;
    }
  } catch (e) {
  }
  return null;
};

const parseAsDocumentUrl = urlStr => {
  // try docshare
  const maybeDocshare = parseAsDocshareUrl(urlStr);
  if (maybeDocshare) { return { docId: maybeDocshare, type: 'docshare' }; }

  // try cnDocs
  const maybeCndocs = parseAsCndocsUrl(urlStr);
  if (maybeCndocs) { return { docId: maybeCndocs, type: 'cndocs' }; }

  // try network filesystem
  const maybeFilesystem = parseAsFilesystemUrl(urlStr);
  if (maybeFilesystem) { return { docId: maybeFilesystem, type: 'filesystem' }; }

  return { docId: null, type: null };
}

export interface Parts {
  parts?: Part[];
  documents?: IDocumentMetadata[];
  attachments?: AttachmentRef[];
}

interface Part {
  text?: string;
  url?: string;
  image?: string;
  imageName?: string;
}

export interface AttachmentRef {
  id: string;
  fileName?: string;
  extension?: string;
}

const imageExtensions = ["png", "jpg", "jpeg"];

const getFileNameFromPath = (value: string): string => {
  const parts = value.split(/[\\/]/);
  return parts[parts.length - 1] || value;
};

const parseAttachmentRef = (input: unknown): AttachmentRef | null => {
  if (input == null) {
    return null;
  }

  if (typeof input === "string") {
    const fileName = getFileNameFromPath(input);
    const extension = (fileName.split(".").pop() || "").toLowerCase();
    return {
      id: input,
      fileName,
      extension,
    };
  }

  if (typeof input === "object") {
    const attachment = input as Record<string, unknown>;
    if (typeof attachment.id !== "string" || !attachment.id) {
      return null;
    }

    const normalizedName = typeof attachment.fileName === "string" && attachment.fileName
      ? attachment.fileName
      : undefined;
    const normalizedExt = typeof attachment.extension === "string" && attachment.extension
      ? attachment.extension.toLowerCase()
      : (normalizedName ? (normalizedName.split(".").pop() || "").toLowerCase() : "");

    return {
      id: attachment.id,
      fileName: normalizedName,
      extension: normalizedExt,
    };
  }

  return null;
}

export const parseMessageForDocumentsAndUrls = (message: string, attachmentsString?: string | any[]): Parts => {
  const s = message || '';
  const urls = findUrls(s);
  const parts = [];
  const documents: { [docId: string]: IDocumentMetadata } = {};
  let lastIndex = 0;
  for (const {index, text, url} of urls) {
    const { docId } = parseAsDocumentUrl(url);
    if (docId != null && documents[docId] == null) {
      const doc: IDocumentMetadata = {id: docId, url};
      documents[docId] = doc;
    }

    if (index > 0) {
      parts.push({text: s.slice(lastIndex, index)});
    }
    parts.push({text, url, docId});
    lastIndex = index + url.length;

  }
  if (lastIndex < s.length) {
    parts.push({text: s.slice(lastIndex)});
  }

  let allAttachments = [];
  try {
    const json = typeof attachmentsString === "string" ? JSON.parse(attachmentsString || '[]') : attachmentsString;
    allAttachments = Array.isArray(json) ? json : [];
  } catch {};
  const attachments: AttachmentRef[] = [];
  for (const rawAttachment of allAttachments) {
    const attachment = parseAttachmentRef(rawAttachment);
    if (!attachment) {
      continue;
    }

    const ext = (attachment.extension || "").toLowerCase();
    if (imageExtensions.includes(ext)) {
      parts.push({ image: attachment.id, imageName: attachment.fileName });
      continue;
    }

    attachments.push(attachment);
  }

  return {parts, documents: Object.values(documents), attachments};
};

export const handleUrls = (urls: string[], commentDocumentMetadatas: any[], taxReturnService: TaxReturnService): Promise<PastedDocMetadata[]> => {

  return new Promise(resolve => {
    if (urls.length === 0) resolve(commentDocumentMetadatas = []);
    const docIds = {};
    const existingMetadataByDocId = {};
    const newMetadataByDocId = {};
    for (const urlStr of urls) {
      const { docId } = parseAsDocumentUrl(urlStr);
      if (docId != null) {
        docIds[docId] = true;
        const existing = commentDocumentMetadatas.find(dm => dm.id === docId);
        if (existing) {
          existingMetadataByDocId[docId] = existing;
        } else {
          newMetadataByDocId[docId] = {id: docId, url: urlStr};
        }
      }
    }

    // update with old and and new
    commentDocumentMetadatas = Object.keys(docIds).map(docId => existingMetadataByDocId[docId] || newMetadataByDocId[docId]);

    // fetch filenames for the unseen doc ids
    const newDocIds = Object.keys(newMetadataByDocId);
    if (newDocIds.length) {
      taxReturnService.getDocumentMetadata(newDocIds.map(encodeURIComponent)).subscribe(resp => {
        updateDocumentCache(resp.results);
        const newMetadatas = [...commentDocumentMetadatas];
        resp.results.forEach(docMetadata => {
          const idx = newMetadatas.findIndex(dm => dm.id === docMetadata.id);
          if (idx >= 0) {
            // check it's still the same object to avoid any race conditions
            const obj = newMetadatas[idx];
            if (obj === newMetadataByDocId[docMetadata.id]) {
              newMetadatas[idx] = {
                ...obj,
                filename: docMetadata.filename,
              };
            }
          }
        });
        resp.errors.forEach(error => {
          const idx = newMetadatas.findIndex(dm => dm.id === error.id);
          if (idx >= 0) {
            // check it's still the same object to avoid any race conditions
            const obj = newMetadatas[idx];
            if (obj === newMetadataByDocId[error.id]) {
              newMetadatas[idx] = {
                ...obj,
                error: error.error,
              };
            }
          }
        });
        resolve(commentDocumentMetadatas = newMetadatas);
      });
    } else {
      resolve(commentDocumentMetadatas);
    }

  });

};

